import { action, computed, makeObservable } from 'mobx';

import { AlertsStore } from '@/configs/store/AlertsStore';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { trackerApi } from '@/tracker/api/trackerApi';
import { CreateBoardRequest, UpdateBoardRequest } from '@/tracker/types';
import { showError } from '@/tracker/utils/showError';

/** Создание, правка и удаление доски. Ошибки бэка (статус в двух колонках и т.п.) — оповещением */
export class BoardSettingsStore extends LocalStore {
  private readonly _alertsStore: AlertsStore;
  private readonly _savingStage = new LoadingStageModel();

  constructor({ alertsStore }: { alertsStore: AlertsStore }) {
    super();
    this._alertsStore = alertsStore;

    makeObservable(this, {
      isSaving: computed,
      create: action.bound,
      update: action.bound,
      remove: action.bound,
    });
  }

  get isSaving() {
    return this._savingStage.isLoading;
  }

  create(data: CreateBoardRequest) {
    return this.run({
      title: 'Не удалось создать доску',
      request: () => trackerApi.createBoard(data),
    });
  }

  update(params: { id: string; data: UpdateBoardRequest }) {
    return this.run({
      title: 'Не удалось сохранить доску',
      request: () => trackerApi.updateBoard(params),
    });
  }

  /** true — удалена */
  async remove(id: string) {
    const result = await this.run({
      title: 'Не удалось удалить доску',
      request: () => trackerApi.deleteBoard(id).then(() => true),
    });

    return !!result;
  }

  /** Результат запроса или null, если не получилось */
  private async run<T>({ title, request }: { title: string; request: () => Promise<T> }) {
    try {
      this._savingStage.loading();

      const result = await request();

      this._savingStage.success();

      return result;
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title, error });

      return null;
    }
  }
}
